import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { articleDetails, type ArticleContent } from '@/mocks/articles';
import { hotRecommendations } from '@/mocks/home';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const article = articleDetails[articleId as keyof typeof articleDetails];

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">内容未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedItems = hotRecommendations.filter((item) =>
    article.relatedArticles.includes(item.id),
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '糖心视频',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 糖心视频`}
        description={article.summary}
        keywords={article.tags.join(', ')}
        canonicalUrl={`https://www.tangxinsp.com/article/${article.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: article.category, path: `/category/${article.categorySlug}` },
                { name: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-xl overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full aspect-[2/1] object-cover"
              />
            </div>

            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                  {article.category}
                </span>
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500 text-sm"></i>
                  <span className="text-sm font-medium text-foreground-700">{article.rating}</span>
                </div>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.publishDate}
                </span>
              </div>
            </header>

            <div className="prose-custom mb-10">
              <p className="text-foreground-700 leading-relaxed mb-6 text-base md:text-lg border-l-4 border-primary-400 pl-4 py-1 bg-primary-50/50 rounded-r-lg">
                {article.summary}
              </p>
              {article.content.map((block: ArticleContent, idx: number) => {
                if (block.type === 'heading') {
                  return (
                    <h2
                      key={idx}
                      className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-8 mb-4"
                    >
                      {block.text}
                    </h2>
                  );
                }
                return (
                  <p
                    key={idx}
                    className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base"
                  >
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2 mb-10 pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 bg-secondary-50 text-secondary-700 text-xs font-medium rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {relatedItems.length > 0 && (
              <section className="pt-6 border-t border-background-200/70">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-5">
                  相关推荐
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {relatedItems.map((item) => (
                    <Link
                      key={item.id}
                      to={`/article/${item.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <h4 className="font-medium text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {item.title}
                      </h4>
                      <span className="text-xs text-foreground-400">{item.category}</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}